import type { CartProduct } from "@features/product/utils/toCartProduct";
import type { Cart } from "@repo/core/cart";

import { Market } from "@features/market/config/i18n-config";
import { createCartItemSkeleton } from "./createCartItemSkeleton";

export default function addToCartOptimistically(
  input: CartProduct[],
  market: Market,
  currentCart?: Cart | null,
): Cart | null {
  if (input.length === 0) {
    return currentCart ?? null;
  }

  const items = [...(currentCart?.items ?? [])];

  for (const product of input) {
    const partNo = product.partNo;
    const index = items.findIndex((item) => item.partNo === partNo);
    const existing = index > -1 ? items[index] : undefined;

    if (existing) {
      const quantity = product.quantity + existing.quantity;
      items[index] = {
        ...existing,
        quantity,
        totalGrossAmount: existing.price * quantity,
      };
      continue;
    }

    items.push(createOptimisticItem(product, product.quantity));
  }

  const productTotal = items.reduce(
    (sum, item) => sum + item.totalGrossAmount,
    0,
  );

  if (!currentCart) {
    return {
      id: 0,
      currencyCode: market.currency,
      items,
      basketPromotions: [],
      totalGrossAmount: productTotal,
      totalVat: 0,
      totalFreight: 0,
      totalFreightInclVat: 0,
    };
  }

  return {
    ...currentCart,
    items,
    totalGrossAmount: productTotal + currentCart.totalFreightInclVat,
  };
}

function createOptimisticItem(product: CartProduct, quantity: number) {
  return createCartItemSkeleton({
    lineNo: 0,
    quantity,
    partNo: product.partNo,
    name: product.name,
    brand: product.brand,
    imageKey: product.imageKey,
    price: product.price,
    totalGrossAmount: product.price * quantity,
  });
}
